import React, { useId, useMemo, useState } from 'react';
import {
  activityChartYMax,
  activityChartYTicks,
  formatActivityBarLabel,
  formatActivityChartTick,
  formatActivityCreatedLabel,
  formatActivityChartDate,
  isAllZeroDaily,
  selectChartXTicks,
} from './adminActivityDisplay';

const WIDTH = 640;
const HEIGHT = 228;
const PAD = { top: 12, right: 10, bottom: 42, left: 36 };

/**
 * Lightweight SVG bar chart. No chart library. Counts are UTC calendar days.
 */
export default function AdminActivityChart({ daily, language, t }) {
  const titleId = useId();
  const [activeDate, setActiveDate] = useState(null);
  const points = Array.isArray(daily) ? daily : [];
  const counts = points.map((row) => row.count);
  const yMax = activityChartYMax(counts);
  const yTicks = activityChartYTicks(yMax);
  const allZero = isAllZeroDaily(points);
  const xTicks = useMemo(
    () => selectChartXTicks(points.map((row) => row.date), 6),
    [points],
  );

  const innerW = WIDTH - PAD.left - PAD.right;
  const innerH = HEIGHT - PAD.top - PAD.bottom;
  const n = Math.max(points.length, 1);
  const gap = points.length > 20 ? 1.5 : 3;
  const barW = Math.max(2, (innerW - gap * (n - 1)) / n);

  const summary = t('admin.activity.chart.summary').replace('{days}', String(points.length));

  return (
    <figure className="rounded-xl border border-slate-200 bg-white p-4">
      <figcaption id={titleId} className="text-sm font-semibold text-slate-800">
        {t('admin.activity.chart.title')}
      </figcaption>
      <p className="mt-1 text-xs text-slate-500">{t('admin.activity.chart.utcHint')}</p>
      {allZero ? (
        <p className="mt-3 text-sm text-slate-600" role="status">
          {t('admin.activity.chart.empty')}
        </p>
      ) : null}
      <div className="mt-3 w-full overflow-hidden">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="h-auto w-full"
          role="img"
          aria-labelledby={titleId}
          aria-label={summary}
        >
          <title>{summary}</title>
          {yTicks.map((tick) => {
            const y = PAD.top + innerH - (tick / yMax) * innerH;
            return (
              <g key={`y-${tick}`}>
                <line
                  x1={PAD.left}
                  x2={WIDTH - PAD.right}
                  y1={y}
                  y2={y}
                  stroke="#e2e8f0"
                  strokeWidth="1"
                />
                <text
                  x={PAD.left - 6}
                  y={y + 3}
                  textAnchor="end"
                  className="fill-slate-500"
                  fontSize="10"
                >
                  {tick}
                </text>
              </g>
            );
          })}
          {points.map((row, index) => {
            const x = PAD.left + index * (barW + gap);
            const barH = (row.count / yMax) * innerH;
            const y = PAD.top + innerH - barH;
            const selected = activeDate === row.date;
            const label = formatActivityBarLabel(row.date, row.count, t, language);
            return (
              <g key={row.date}>
                <rect
                  x={x}
                  y={barH === 0 ? PAD.top + innerH - 2 : y}
                  width={barW}
                  height={barH === 0 ? 2 : barH}
                  rx="1"
                  className={selected ? 'fill-indigo-700' : 'fill-indigo-500'}
                  tabIndex={0}
                  role="listitem"
                  aria-label={label}
                  onMouseEnter={() => setActiveDate(row.date)}
                  onMouseLeave={() => setActiveDate(null)}
                  onFocus={() => setActiveDate(row.date)}
                  onBlur={() => setActiveDate(null)}
                >
                  <title>{label}</title>
                </rect>
              </g>
            );
          })}
          {xTicks.map((date) => {
            const index = points.findIndex((row) => row.date === date);
            if (index < 0) return null;
            const x = PAD.left + index * (barW + gap) + barW / 2;
            return (
              <text
                key={`x-${date}`}
                x={x}
                y={HEIGHT - 14}
                textAnchor="middle"
                className="fill-slate-500"
                fontSize="10"
              >
                {formatActivityChartTick(date, language)}
              </text>
            );
          })}
        </svg>
      </div>
      {activeDate ? (
        <p className="mt-2 hidden text-xs text-slate-600 md:block" aria-live="polite">
          <span className="font-medium text-slate-800">
            {formatActivityChartDate(activeDate, language)}
          </span>
          {' · '}
          {formatActivityCreatedLabel(
            points.find((row) => row.date === activeDate)?.count,
            t,
            language,
          )}
        </p>
      ) : (
        <p className="mt-2 hidden text-xs text-slate-500 md:block">{t('admin.activity.chart.yAxis')}</p>
      )}
      {points.length ? (
        <ul
          className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600 sm:grid-cols-3 md:hidden"
          aria-label={t('admin.activity.chart.dailyList')}
        >
          {points.map((row) => (
            <li key={`list-${row.date}`} className="flex justify-between gap-2 tabular-nums">
              <span>{formatActivityChartTick(row.date, language)}</span>
              <span>{row.count}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </figure>
  );
}
